import {
  BookOpen,
  Calendar,
  FolderOpen,
  HelpCircle,
  IndianRupee,
  LogOut,
  Settings,
  User,
  Users,
} from 'lucide-react'
import type { JSX } from 'react/jsx-runtime'
import type { NavItem } from '@/types'
import { NavLink } from 'react-router-dom'
import { routes } from '@/common/constants/routes'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { useProfile } from '@/features/profile/hooks/use-profile'

// ...existing code...

export const Sidebar = (): JSX.Element => {
  const { signOut } = useAuth()
  const { profile } = useProfile()

  const fullName = profile?.full_name ?? 'Advocate'
  const initials = fullName
    .split(' ')
    .filter(Boolean)
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const navItems: NavItem[] = [
    { icon: FolderOpen, id: 'cases', label: 'Cases', path: routes.CASES },
    {
      icon: Calendar,
      id: 'calendar',
      label: 'Calendar',
      path: routes.CALENDAR,
    },
    { icon: Users, id: 'clients', label: 'Clients', path: routes.CLIENTS },
    { icon: User, id: 'team', label: 'Team', path: routes.TEAM },
    {
      icon: IndianRupee,
      id: 'financials',
      label: 'Financials',
      path: routes.BILLING,
    },
    {
      icon: BookOpen,
      id: 'research',
      label: 'Research',
      path: routes.RESEARCH,
    },
    {
      icon: Settings,
      id: 'settings',
      label: 'Settings',
      path: routes.SETTINGS,
    },
    { icon: HelpCircle, id: 'help', label: 'Help', path: routes.HELP },
  ]

  return (
    <ShadcnSidebar variant="inset">
      <SidebarHeader className="p-6">
        <div className="flex items-center gap-3">
          <div
            className="
              flex h-10 w-10 items-center justify-center rounded-xl bg-teal-600
              text-xl font-bold text-white shadow-lg shadow-teal-100
              dark:shadow-none
            "
          >
            V
          </div>
          <div className="flex flex-col">
            <h1
              className="
                leading-tight font-bold text-gray-900
                dark:text-gray-100
              "
            >
              Vakeel Near Me
            </h1>
            <p
              className="
                text-[10px] font-bold tracking-wider text-gray-400 uppercase
                dark:text-gray-500
              "
            >
              Advocate Portal
            </p>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="gap-1 px-2">
              {navItems.map((item) => {
                return (
                  <SidebarMenuItem key={item.id}>
                    <NavLink className="w-full" to={item.path}>
                      {({ isActive }) => {
                        return (
                          <SidebarMenuButton
                            className="h-12 rounded-xl px-4"
                            isActive={isActive}
                            tooltip={item.label}
                          >
                            <item.icon
                              className={isActive ? 'text-teal-600' : ''}
                            />
                            <span
                              className={
                                isActive ? 'font-bold text-teal-600' : ''
                              }
                            >
                              {item.label}
                            </span>
                            {isActive && (
                              <div
                                className="
                                  ml-auto h-1.5 w-1.5 rounded-full bg-teal-600
                                "
                              />
                            )}
                          </SidebarMenuButton>
                        )
                      }}
                    </NavLink>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="gap-4 p-4">
        <SidebarMenu>
          <SidebarMenuItem>
            <NavLink className="w-full" to={routes.PROFILE}>
              {({ isActive }) => {
                return (
                  <SidebarMenuButton
                    isActive={isActive}
                    className="
                      h-auto rounded-2xl p-3
                      hover:bg-teal-50/50
                    "
                  >
                    <Avatar
                      className="
                        h-10 w-10 border-2 border-white shadow-sm
                        dark:border-gray-700
                      "
                    >
                      <AvatarFallback
                        className="bg-teal-500 font-bold text-white"
                      >
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p
                        className="
                          truncate text-sm font-bold text-gray-900
                          dark:text-gray-100
                        "
                      >
                        {fullName}
                      </p>
                      <p
                        className="
                          truncate text-[10px] font-bold text-gray-400 uppercase
                          dark:text-gray-500
                        "
                      >
                        Lead Counsel
                      </p>
                    </div>
                  </SidebarMenuButton>
                )
              }}
            </NavLink>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              className="
                w-full justify-start gap-3 rounded-xl py-6 font-bold
                text-red-500
                hover:bg-red-50 hover:text-red-600
                dark:text-red-400
                dark:hover:bg-red-900/20 dark:hover:text-red-300
              "
              onClick={() => void signOut()}
            >
              <LogOut className="size-5" />
              <span>Log out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </ShadcnSidebar>
  )
}
